/**
 * Payline Error Handling
 * Maps Payline error codes to user-friendly messages
 */

export class PaylineError extends Error {
  constructor(code, message, details = {}) {
    super(message);
    this.name = 'PaylineError';
    this.code = code;
    this.details = details;
  }
}

/**
 * Map of Payline error codes to user-friendly messages
 * Includes both transaction errors and system errors
 */
export const PAYLINE_ERROR_MESSAGES = {
  // Success
  '00000': {
    message: 'Paiement accepté',
    severity: 'success',
    userMessage: 'Votre paiement a été accepté.',
  },

  // Card errors
  '02306': {
    message: 'Carte expirée',
    severity: 'error',
    userMessage: 'Cette carte a expiré. Veuillez utiliser une autre carte.',
  },
  '02307': {
    message: 'Numéro de carte invalide',
    severity: 'error',
    userMessage: 'Le numéro de carte n\'est pas valide.',
  },
  '02308': {
    message: 'Solde insuffisant',
    severity: 'error',
    userMessage: 'Solde insuffisant. Veuillez utiliser une autre carte.',
  },

  // 3D Secure errors
  '02310': {
    message: '3D Secure - Authentification échouée',
    severity: 'error',
    userMessage: 'L\'authentification 3D Secure a échoué. Veuillez réessayer.',
  },
  '02311': {
    message: '3D Secure - Authentification annulée',
    severity: 'error',
    userMessage: 'L\'authentification 3D Secure a été annulée.',
  },

  // Subscriber/token errors
  '02401': {
    message: 'Abonné non trouvé',
    severity: 'error',
    userMessage: 'Cette carte n\'est plus disponible. Veuillez enregistrer une nouvelle carte.',
  },
  '02501': {
    message: 'Transaction non trouvée',
    severity: 'error',
    userMessage: 'La transaction n\'a pas pu être trouvée. Veuillez réessayer.',
  },

  // Merchant configuration errors
  '02301': {
    message: 'Commerçant invalide',
    severity: 'error',
    userMessage: 'Configuration du commerçant invalide. Veuillez contacter le support.',
  },
  '02302': {
    message: 'Contrat invalide',
    severity: 'error',
    userMessage: 'Configuration du contrat invalide. Veuillez contacter le support.',
  },
  '02303': {
    message: 'Montant invalide',
    severity: 'error',
    userMessage: 'Le montant du paiement n\'est pas valide.',
  },

  // Generic errors
  NETWORK_ERROR: {
    message: 'Erreur réseau',
    severity: 'error',
    userMessage: 'Erreur réseau. Veuillez vérifier votre connexion Internet.',
  },
  TIMEOUT_ERROR: {
    message: 'Délai d\'attente dépassé',
    severity: 'error',
    userMessage: 'Le paiement a pris trop de temps. Veuillez réessayer.',
  },
  UNKNOWN_ERROR: {
    message: 'Erreur inconnue',
    severity: 'error',
    userMessage: 'Une erreur inattendue s\'est produite. Veuillez réessayer.',
  },
};

/**
 * Get error details by code
 * @param {string} code - Error code from Payline API
 * @returns {object} Error details with message, severity, userMessage
 */
export const getErrorDetails = (code) => PAYLINE_ERROR_MESSAGES[code] || PAYLINE_ERROR_MESSAGES.UNKNOWN_ERROR;

/**
 * Handle Payline API error response
 * @param {object} error - Error from GraphQL/API call
 * @param {object} response - Response data (if available)
 * @returns {PaylineError} - Normalized PaylineError
 */
export const handlePaylineError = (error, response = {}) => {
  // Handle GraphQL errors
  if (error && error.graphQLErrors && error.graphQLErrors.length > 0) {
    const gqlError = error.graphQLErrors[0];
    const code = (response && response.resultCode) || gqlError.message;
    const details = getErrorDetails(code);
    return new PaylineError(code, details.message, {
      userMessage: details.userMessage,
      severity: details.severity,
      originalError: gqlError,
    });
  }

  // Handle network errors
  if (error && error.networkError) {
    const details = getErrorDetails('NETWORK_ERROR');
    return new PaylineError('NETWORK_ERROR', details.message, {
      userMessage: details.userMessage,
      severity: details.severity,
      originalError: error.networkError,
    });
  }

  // Handle Payline API response errors (resultCode indicates failure)
  if (response && response.resultCode && response.resultCode !== '00000') {
    const details = getErrorDetails(response.resultCode);
    return new PaylineError(response.resultCode, details.message, {
      userMessage: details.userMessage,
      severity: details.severity,
      response,
    });
  }

  // Handle generic errors
  const details = getErrorDetails('UNKNOWN_ERROR');
  return new PaylineError('UNKNOWN_ERROR', details.message, {
    userMessage: details.userMessage,
    severity: details.severity,
    originalError: error,
  });
};

/**
 * Check if response indicates success
 * @param {object} response - Payline API response
 * @returns {boolean} - True if resultCode is '00000'
 */
export const isPaylineSuccess = (response) => response && (response.resultCode === '00000' || response.resultCode === '02500');

/**
 * Safe API call wrapper with error handling
 * @param {function} apiCall - Promise-based API function
 * @param {object} context - Context (for logging, etc.)
 * @returns {Promise} - Resolves with response or rejects with PaylineError
 */
export const safePaylineCall = async (apiCall) => {
  try {
    const response = await apiCall();

    // Check if response indicates API-level error
    if (response && !isPaylineSuccess(response)) {
      throw handlePaylineError(null, response);
    }

    return response;
  } catch (error) {
    if (error instanceof PaylineError) {
      throw error;
    }
    throw handlePaylineError(error);
  }
};
